import React, { ChangeEvent, useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import styles from './ExploreMobile.module.css';
import ExploreItem from './ExploreItem';
import ScenarioItem from './ScenarioItem';
import SearchBar from './SearchBar';
import SearchDropdown from './SearchDropdown';
import CategoryTabs from './CategoryTabs';
import LoadingIndicator from './LoadingIndicator';
import MobileGridLoadingIndicator from './MobileGridLoadingIndicator';
import { Character } from './ExploreDesktop';
import { ScenarioDefinitionListResponse } from '../../api/types/scenario';
import FeaturedScenarioCarousel from './FeaturedScenarioCarousel';
import FeaturedCharacters from './FeaturedCharacters';
import { FaUser, FaBookOpen } from 'react-icons/fa';
import { logoIcon } from '../../assets/icons';

/**
 * Props interface for the ExploreMobile component
 */
interface ExploreMobileProps {
  characters: Character[];
  scenarios: ScenarioDefinitionListResponse[];
  featuredCharacters?: Character[];
  featuredScenarios?: ScenarioDefinitionListResponse[];
  dropdownCharacters: Character[]; // New prop for dropdown results
  dropdownScenarios: ScenarioDefinitionListResponse[]; // New prop for dropdown results
  searchQuery: string;
  activeSearchQuery?: string;
  activeCategory: string;
  categories: string[];
  contentType: 'characters' | 'scenarios';
  contentTypes: { id: 'characters' | 'scenarios', label: string }[];
  isLoading: boolean;
  error?: string | null;
  sidebarExpanded?: boolean;
  isSearchActive?: boolean;
  handleSearch: (query: string) => void;
  handleSearchSubmit: (event: FormEvent) => void;
  executeSearch: () => void;
  clearSearch: () => void;
  handleCategoryChange: (category: string) => void;
  handleContentTypeChange: (type: 'characters' | 'scenarios') => void;
  handleCharacterClick: (character: Character) => void;
  handleScenarioClick: (scenario: ScenarioDefinitionListResponse) => void;
}

// Configuration constants
const MAX_FEATURED_ITEMS = 20; // Maximum number of featured items to display (increased for two-row layout)

/**
 * Mobile view for the Explore feature
 * Optimized for smaller screens with a vertical layout
 */
const ExploreMobile: React.FC<ExploreMobileProps> = ({
  characters,
  scenarios,
  featuredCharacters,
  featuredScenarios,
  dropdownCharacters,
  dropdownScenarios,
  searchQuery,
  activeSearchQuery = '',
  activeCategory,
  categories,
  contentType,
  contentTypes,
  isLoading,
  error,
  sidebarExpanded = false,
  isSearchActive = false,
  handleSearch,
  handleSearchSubmit,
  executeSearch,
  clearSearch,
  handleCategoryChange,
  handleContentTypeChange,
  handleCharacterClick,
  handleScenarioClick
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [displayedFeaturedCharacters, setDisplayedFeaturedCharacters] = useState<Character[]>([]);
  const [displayedFeaturedScenarios, setDisplayedFeaturedScenarios] = useState<ScenarioDefinitionListResponse[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Add a ref to the header container
  const headerRef = useRef<HTMLDivElement>(null);

  // Update local search query when prop changes
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Handle search input change - just passes to parent
  const handleLocalSearch = (query: string) => {
    setLocalSearchQuery(query);
    handleSearch(query);
  };

  // Initialize featured content when data is first available
  useEffect(() => {
    // Only set initial featured content once
    if (!initialDataLoaded) {
      // For characters
      if (featuredCharacters && featuredCharacters.length) {
        setDisplayedFeaturedCharacters(featuredCharacters);
      } else if (characters.length) {
        setDisplayedFeaturedCharacters(characters.slice(0, MAX_FEATURED_ITEMS));
      }
      
      // For scenarios
      if (featuredScenarios && featuredScenarios.length) {
        setDisplayedFeaturedScenarios(featuredScenarios);
      } else if (scenarios.length) {
        setDisplayedFeaturedScenarios(scenarios.slice(0, MAX_FEATURED_ITEMS));
      }
      
      // If we have either characters or scenarios, mark as loaded
      if (characters.length || scenarios.length) {
        setInitialDataLoaded(true);
      }
    }
  }, [featuredCharacters, featuredScenarios, characters, scenarios, initialDataLoaded]);
  
  // Update featured content if the featuredCharacters/featuredScenarios props change
  useEffect(() => {
    if (featuredCharacters && featuredCharacters.length) {
      setDisplayedFeaturedCharacters(featuredCharacters);
    }
    
    if (featuredScenarios && featuredScenarios.length) {
      setDisplayedFeaturedScenarios(featuredScenarios);
    } else if (scenarios.length && displayedFeaturedScenarios.length === 0) {
      // Fallback: If we still don't have featured scenarios but we have regular scenarios
      setDisplayedFeaturedScenarios(scenarios.slice(0, MAX_FEATURED_ITEMS));
    }
  }, [featuredCharacters, featuredScenarios, scenarios, displayedFeaturedScenarios.length]);

  // Update header class based on both scroll position and sidebar state
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        // Add or remove scrolled class based on scroll position
        if (window.scrollY > 20) {
          headerRef.current.classList.add(styles.scrolled);
        } else {
          headerRef.current.classList.remove(styles.scrolled);
        }
        
        // Add or remove sidebarExpanded class based on sidebar state
        if (sidebarExpanded) {
          headerRef.current.classList.add(styles.sidebarExpanded);
        } else {
          headerRef.current.classList.remove(styles.sidebarExpanded);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Call once to set initial state
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sidebarExpanded]);

  // Render content based on content type
  const renderContent = () => {
    if (error) {
      return <div className={styles.errorMessage}>{error}</div>;
    }
    
    if (contentType === 'characters') {
      return characters.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No characters found</p>
        </div>
      ) : (
        characters.map((character) => (
          <ExploreItem
            key={character.id}
            character={character}
            onClick={handleCharacterClick}
          />
        ))
      );
    } else {
      return scenarios.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No scenarios found</p>
        </div>
      ) : (
        scenarios.map((scenario) => (
          <ScenarioItem
            key={scenario.id}
            scenario={scenario}
            onClick={handleScenarioClick}
          />
        ))
      );
    }
  };

  return (
    <div className={styles.exploreMobilePage}>
      {/* Header section with toast icon and search bar */}
      <div className={`${styles.headerContainer} ${sidebarExpanded ? styles.sidebarExpanded : ''}`} ref={headerRef}>
        {/* Mobile Search Dropdown - Left aligned */}
        <div className={styles.searchContainer}>
          <SearchDropdown
            dropdownCharacters={dropdownCharacters}
            dropdownScenarios={dropdownScenarios}
            searchQuery={localSearchQuery}
            activeSearchQuery={activeSearchQuery}
            isLoading={isLoading}
            handleSearch={handleLocalSearch}
            executeSearch={executeSearch}
            clearSearch={clearSearch}
            handleCharacterClick={handleCharacterClick}
            handleScenarioClick={handleScenarioClick}
          />
        </div>
        
        {/* Toast Icon in top right corner with Discord link */}
        <div className={styles.toastIconContainer}>
          <a 
            href="https://discord.gg/qN5ugTpfYP" 
            target="_blank" 
            rel="noopener noreferrer" 
            aria-label="Join our Discord community"
          >
            <img src={logoIcon} alt="Hangjam Toast" className={styles.toastIcon} />
          </a>
        </div>
      </div>
      
      {/* Active Search Indicator */}
      {activeSearchQuery && (
        <div className={styles.activeSearchIndicator}>
          <span>Showing results for: "{activeSearchQuery}"</span>
          <button 
            type="button" 
            className={styles.clearSearchButton}
            onClick={clearSearch}
          >
            Clear
          </button>
        </div>
      )}
      
      {/* Featured Scenarios Carousel */}
      {displayedFeaturedScenarios.length > 0 && !activeSearchQuery && (
        <FeaturedScenarioCarousel
          featuredScenarios={displayedFeaturedScenarios}
          onScenarioClick={handleScenarioClick}
        />
      )}
      
      {/* Featured Characters (simplified for mobile) */}
      {!activeSearchQuery && (
        <FeaturedCharacters 
          characters={displayedFeaturedCharacters}
          onCharacterClick={handleCharacterClick}
        />
      )}
      
      {/* Content Type Selector - Updated to match desktop styling */}
      <div className={styles.contentTypeSelector}>
        {contentTypes.map((type) => (
          <button 
            key={type.id} 
            className={`${styles.contentTypeButton} ${type.id === contentType ? styles.active : ''}`}
            onClick={() => handleContentTypeChange(type.id)}
            aria-pressed={type.id === contentType}
            aria-label={type.label}
          >
            <span className={styles.contentTypeIcon}>
              {type.id === 'characters' ? <FaUser /> : <FaBookOpen />}
            </span>
            <span className={styles.contentTypeLabel}>{type.label}</span>
          </button>
        ))}
      </div>
      
      {/* Category Tabs - Show for both characters and scenarios */}
      <CategoryTabs 
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        className={styles.categoryTabs}
      />
      
      {/* Content Header */}
      <div className={styles.contentHeader}>
        <h2 className={styles.contentTitle}>
          {contentType === 'characters' ? 'Characters' : 'Scenarios'}
          {activeSearchQuery && <span className={styles.searchResultsLabel}> - Search Results</span>}
        </h2>
      </div>
      
      {/* Content Grid - Always visible regardless of search */}
      <div className={styles.contentGrid}>
        {isLoading ? (
          <MobileGridLoadingIndicator />
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

export default ExploreMobile; 